import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './QuestionsTab.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function SortableReorderItem({ item, index }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`reorder-list-item ${isDragging ? 'reorder-list-item-dragging' : ''}`}
    >
      <span className="reorder-handle" {...attributes} {...listeners} title="Перетащите для изменения порядка">
        ⋮⋮
      </span>
      <span className="reorder-index">{index + 1}</span>
      <span className="reorder-text">{item.text.length > 80 ? item.text.slice(0, 80) + '…' : item.text}</span>
    </div>
  );
}

function WelcomeQuestionsListTab() {
  const [questions, setQuestions] = useState([]);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [formData, setFormData] = useState({
    topic_id: '',
    text: '',
    category: '',
    correct_answer: '',
  });
  const [nextOrder, setNextOrder] = useState(null);
  const [showReorderModal, setShowReorderModal] = useState(false);
  const [reorderStep, setReorderStep] = useState('select'); // 'select' | 'list'
  const [reorderTopicId, setReorderTopicId] = useState('');
  const [reorderCategory, setReorderCategory] = useState('');
  const [reorderList, setReorderList] = useState([]);
  const [reorderLoading, setReorderLoading] = useState(false);
  const [reorderSaving, setReorderSaving] = useState(false);
  const [regeneratingAnswer, setRegeneratingAnswer] = useState(false);

  const [makeMainQuestion, setMakeMainQuestion] = useState(null);
  const [makeMainFormData, setMakeMainFormData] = useState({ topic_id: '', text: '', category: '', correct_answer: '' });
  const [makeMainRegenerating, setMakeMainRegenerating] = useState(false);
  const [makeMainSaving, setMakeMainSaving] = useState(false);

  const [filters, setFilters] = useState({
    topic_id: '',
    category: '',
    search: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(20);

  useEffect(() => {
    fetchTopics();
  }, []);

  useEffect(() => {
    fetchData();
  }, [currentPage, filters]);

  useEffect(() => {
    if (showForm && !editingQuestion && formData.topic_id) {
      const params = { topic_id: parseInt(formData.topic_id, 10) };
      if (formData.category) params.category = formData.category;
      axios.get(`${API_URL}/api/welcome-questions/next-order`, { params })
        .then((res) => setNextOrder(res.data.next_order))
        .catch(() => setNextOrder(null));
    } else {
      setNextOrder(null);
    }
  }, [showForm, editingQuestion, formData.topic_id, formData.category]);

  const fetchTopics = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/topics`);
      setTopics(response.data);
    } catch (err) {
      console.error('Ошибка при загрузке тем:', err);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * pageSize;
      const params = { skip, limit: pageSize };
      if (filters.topic_id) params.topic_id = parseInt(filters.topic_id);
      if (filters.category) params.category = filters.category;
      if (filters.search) params.search = filters.search;

      const [questionsRes, countRes] = await Promise.all([
        axios.get(`${API_URL}/api/welcome-questions`, { params }),
        axios.get(`${API_URL}/api/welcome-questions/count`, {
          params: { ...params, skip: undefined, limit: undefined }
        })
      ]);

      setQuestions(questionsRes.data);
      setTotalCount(countRes.data.count);
      setError(null);
    } catch (err) {
      setError('Ошибка при загрузке данных: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        topic_id: parseInt(formData.topic_id, 10),
        text: formData.text,
        category: formData.category || null,
        correct_answer: formData.correct_answer || null,
      };

      if (editingQuestion) {
        await axios.put(`${API_URL}/api/welcome-questions/${editingQuestion.id}`, payload);
      } else {
        await axios.post(`${API_URL}/api/welcome-questions`, payload);
      }
      setShowForm(false);
      setEditingQuestion(null);
      setFormData({ topic_id: '', text: '', category: '', correct_answer: '' });
      fetchData();
    } catch (err) {
      setError('Ошибка при сохранении: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleEdit = (question) => {
    setEditingQuestion(question);
    setFormData({
      topic_id: question.topic_id.toString(),
      text: question.text,
      category: question.category || '',
      correct_answer: question.correct_answer || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить этот welcome-вопрос?')) {
      try {
        await axios.delete(`${API_URL}/api/welcome-questions/${id}`);
        fetchData();
      } catch (err) {
        setError('Ошибка при удалении: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handleRegenerateAnswer = async () => {
    const questionText = (formData.text || '').trim();
    if (!questionText) {
      setError('Введите текст вопроса для пересоздания ответа');
      return;
    }
    try {
      setRegeneratingAnswer(true);
      setError(null);
      const response = await axios.post(`${API_URL}/api/questions/regenerate-answer`, {
        question_text: questionText,
      });
      setFormData((prev) => ({ ...prev, correct_answer: response.data.correct_answer || '' }));
    } catch (err) {
      setError('Ошибка при пересоздании ответа: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRegeneratingAnswer(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingQuestion(null);
    setRegeneratingAnswer(false);
    setFormData({ topic_id: '', text: '', category: '', correct_answer: '' });
  };

  const handleOpenMakeMain = (question) => {
    setMakeMainQuestion(question);
    setMakeMainFormData({
      topic_id: question.topic_id.toString(),
      text: question.text || '',
      category: question.category || '',
      correct_answer: question.correct_answer || '',
    });
    setError(null);
  };

  const handleCloseMakeMain = () => {
    setMakeMainQuestion(null);
    setMakeMainFormData({ topic_id: '', text: '', category: '', correct_answer: '' });
    setMakeMainRegenerating(false);
    setMakeMainSaving(false);
  };

  const handleMakeMainRegenerateAnswer = async () => {
    const questionText = (makeMainFormData.text || '').trim();
    if (!questionText) {
      setError('Введите текст вопроса для пересоздания ответа');
      return;
    }
    try {
      setMakeMainRegenerating(true);
      setError(null);
      const response = await axios.post(`${API_URL}/api/questions/regenerate-answer`, {
        question_text: questionText,
      });
      setMakeMainFormData((prev) => ({ ...prev, correct_answer: response.data.correct_answer || '' }));
    } catch (err) {
      setError('Ошибка при пересоздании ответа: ' + (err.response?.data?.detail || err.message));
    } finally {
      setMakeMainRegenerating(false);
    }
  };

  const handleMakeMainSubmit = async (e) => {
    e.preventDefault();
    if (!makeMainQuestion) return;
    try {
      setMakeMainSaving(true);
      setError(null);
      await axios.post(`${API_URL}/api/questions`, {
        topic_id: parseInt(makeMainFormData.topic_id, 10),
        text: makeMainFormData.text.trim(),
        category: makeMainFormData.category || null,
        correct_answer: makeMainFormData.correct_answer.trim() || null,
      });
      await axios.delete(`${API_URL}/api/welcome-questions/${makeMainQuestion.id}`);
      handleCloseMakeMain();
      fetchData();
    } catch (err) {
      setError('Ошибка при переносе в основную базу: ' + (err.response?.data?.detail || err.message));
    } finally {
      setMakeMainSaving(false);
    }
  };

  const openReorderModal = () => {
    setShowReorderModal(true);
    setReorderStep('select');
    setReorderTopicId('');
    setReorderCategory('');
    setReorderList([]);
  };

  const loadReorderList = async () => {
    if (!reorderTopicId) return;
    setReorderLoading(true);
    try {
      const params = { topic_id: parseInt(reorderTopicId, 10), limit: 500 };
      if (reorderCategory) params.category = reorderCategory;
      const res = await axios.get(`${API_URL}/api/welcome-questions`, { params });
      setReorderList(res.data);
      setReorderStep('list');
    } catch (err) {
      setError('Ошибка при загрузке списка для сортировки: ' + (err.response?.data?.detail || err.message));
    } finally {
      setReorderLoading(false);
    }
  };

  const handleReorderDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setReorderList((items) => {
      const oldIndex = items.findIndex((i) => i.id === active.id);
      const newIndex = items.findIndex((i) => i.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return items;
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  const saveReorder = async () => {
    if (!reorderTopicId || reorderList.length === 0) {
      setShowReorderModal(false);
      return;
    }
    setReorderSaving(true);
    try {
      const payload = {
        topic_id: parseInt(reorderTopicId, 10),
        ordered_ids: reorderList.map((q) => q.id),
      };
      if (reorderCategory) payload.category = reorderCategory;
      await axios.post(`${API_URL}/api/welcome-questions/reorder`, payload);
      setShowReorderModal(false);
      setReorderStep('select');
      fetchData();
    } catch (err) {
      setError('Ошибка при сохранении порядка: ' + (err.response?.data?.detail || err.message));
    } finally {
      setReorderSaving(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const handleSearchChange = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({ topic_id: '', category: '', search: '' });
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const reorderSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  if (loading && questions.length === 0) {
    return <div className="loading">Загрузка...</div>;
  }

  return (
    <div className="questions-list-tab welcome-questions-list-tab">
      <div className="tab-header">
        <h3>Welcome-вопросы (для неавторизованных пользователей)</h3>
        <div className="tab-header-actions">
          <button
            className="btn btn-primary"
            onClick={() => { setShowForm(!showForm); if (showForm) { setEditingQuestion(null); setFormData({ topic_id: '', text: '', category: '', correct_answer: '' }); } }}
          >
            {showForm ? '− Скрыть форму' : '+ Добавить welcome-вопрос'}
          </button>
          <button className="btn btn-secondary btn-reorder" onClick={openReorderModal} title="Изменить порядок вопросов по теме и категории">
            Изменить порядок
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <div className="form-container form-container-inline">
          <h4>{editingQuestion ? 'Редактировать welcome-вопрос' : 'Добавить welcome-вопрос'}</h4>
          <form onSubmit={handleSubmit}>
            <div className="form-row-inline">
              <div className="form-group">
                <label htmlFor="welcome-topic_id">Тема *</label>
                <select
                  id="welcome-topic_id"
                  value={formData.topic_id}
                  onChange={(e) => setFormData({ ...formData, topic_id: e.target.value })}
                  required
                  disabled={topics.length === 0}
                >
                  <option value="">Выберите тему</option>
                  {topics.map((topic) => (
                    <option key={topic.id} value={topic.id}>
                      {topic.title}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="welcome-category">Категория</label>
                <select
                  id="welcome-category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                >
                  <option value="">Выберите категорию</option>
                  <option value="Junior">Junior</option>
                  <option value="Middle">Middle</option>
                  <option value="Senior">Senior</option>
                </select>
              </div>
              {!editingQuestion && formData.topic_id && nextOrder !== null && (
                <div className="form-group next-order-hint">
                  Будет добавлен под номером: <strong>{nextOrder + 1}</strong>
                </div>
              )}
            </div>
            <div className="form-group">
              <label htmlFor="welcome-text">Текст вопроса *</label>
              <textarea
                id="welcome-text"
                value={formData.text}
                onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                required
                rows={3}
                placeholder="Введите текст вопроса"
              />
            </div>
            <div className="form-group">
              <div className="form-group-label-row">
                <label htmlFor="welcome-correct_answer">Правильный ответ</label>
                <button
                  type="button"
                  className="btn btn-secondary btn-regenerate-answer"
                  onClick={handleRegenerateAnswer}
                  disabled={regeneratingAnswer || !(formData.text || '').trim()}
                  title={!(formData.text || '').trim() ? 'Сначала введите текст вопроса' : 'Пересоздать эталонный ответ нейросетью'}
                >
                  {regeneratingAnswer ? '⏳ Пересоздание...' : '🔄 Пересоздать ответ'}
                </button>
              </div>
              <textarea
                id="welcome-correct_answer"
                value={formData.correct_answer}
                onChange={(e) => setFormData({ ...formData, correct_answer: e.target.value })}
                rows={2}
                placeholder="Введите правильный ответ (необязательно)"
                disabled={regeneratingAnswer}
              />
              <small className="form-hint">
                Кнопка «Пересоздать ответ» отправит текст вопроса в нейросеть и подставит эталонный ответ в поле выше. После нажатия «Сохранить» вопрос и ответ сохранятся в БД.
              </small>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={topics.length === 0}>
                {editingQuestion ? 'Сохранить' : 'Создать'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Отмена
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="filters-container">
        <div className="filters-row">
          <div className="filter-group">
            <label htmlFor="welcome-filter-topic">Тема:</label>
            <select
              id="welcome-filter-topic"
              value={filters.topic_id}
              onChange={(e) => handleFilterChange('topic_id', e.target.value)}
              className="filter-select"
            >
              <option value="">Все темы</option>
              {topics.map((topic) => (
                <option key={topic.id} value={topic.id}>
                  {topic.title}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label htmlFor="welcome-filter-category">Категория:</label>
            <select
              id="welcome-filter-category"
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="filter-select"
            >
              <option value="">Все категории</option>
              <option value="Junior">Junior</option>
              <option value="Middle">Middle</option>
              <option value="Senior">Senior</option>
            </select>
          </div>
          <div className="filter-group filter-search">
            <label htmlFor="welcome-filter-search">Поиск:</label>
            <input
              id="welcome-filter-search"
              type="text"
              value={filters.search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Поиск по тексту вопроса..."
              className="filter-input"
            />
          </div>
          <button
            className="btn btn-secondary btn-clear-filters"
            onClick={clearFilters}
            disabled={!filters.topic_id && !filters.category && !filters.search}
          >
            Сбросить фильтры
          </button>
        </div>
        <div className="results-info">
          Найдено вопросов: <strong>{totalCount}</strong>
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Порядок</th>
              <th>Тема</th>
              <th>Категория</th>
              <th>Текст вопроса</th>
              <th>Правильный ответ</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="empty-state">Загрузка...</td>
              </tr>
            ) : questions.length === 0 ? (
              <tr>
                <td colSpan="7" className="empty-state">
                  {filters.topic_id || filters.category || filters.search
                    ? 'Вопросы не найдены по заданным фильтрам'
                    : 'Нет welcome-вопросов. Добавьте первый.'}
                </td>
              </tr>
            ) : (
              questions.map((q) => (
                <tr key={q.id}>
                  <td>{q.id}</td>
                  <td>{q.sort_order}</td>
                  <td>{q.topic_title ?? `ID: ${q.topic_id}`}</td>
                  <td>
                    {q.category ? (
                      <span className="category-badge">{q.category}</span>
                    ) : (
                      <span className="category-empty">—</span>
                    )}
                  </td>
                  <td className="question-text">{q.text}</td>
                  <td className="question-text">
                    {q.correct_answer ? q.correct_answer : <span className="category-empty">—</span>}
                  </td>
                  <td>
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(q)}
                      title="Редактировать"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-icon btn-make-main"
                      onClick={() => handleOpenMakeMain(q)}
                      title="Сделать основным вопросом (перенос в основную базу и удаление из Welcome)"
                    >
                      В основную
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(q.id)}
                      title="Удалить"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {makeMainQuestion && (
        <div className="form-container make-main-form">
          <h3>Перенос вопроса в основную базу</h3>
          <p className="form-hint make-main-hint">
            Вопрос будет добавлен в основную таблицу вопросов и удалён из Welcome-вопросов. Заполните поля при необходимости и нажмите «Сделать основным вопросом».
          </p>
          <form onSubmit={handleMakeMainSubmit}>
            <div className="form-group">
              <label htmlFor="make-main-topic_id">Тема *</label>
              <select
                id="make-main-topic_id"
                value={makeMainFormData.topic_id}
                onChange={(e) => setMakeMainFormData({ ...makeMainFormData, topic_id: e.target.value })}
                required
                disabled={topics.length === 0}
              >
                <option value="">Выберите тему</option>
                {topics.map((topic) => (
                  <option key={topic.id} value={topic.id}>
                    {topic.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="make-main-category">Категория</label>
              <select
                id="make-main-category"
                value={makeMainFormData.category}
                onChange={(e) => setMakeMainFormData({ ...makeMainFormData, category: e.target.value })}
              >
                <option value="">Выберите категорию</option>
                <option value="Junior">Junior</option>
                <option value="Middle">Middle</option>
                <option value="Senior">Senior</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="make-main-text">Текст вопроса *</label>
              <textarea
                id="make-main-text"
                value={makeMainFormData.text}
                onChange={(e) => setMakeMainFormData({ ...makeMainFormData, text: e.target.value })}
                required
                rows={4}
                placeholder="Текст вопроса"
              />
            </div>
            <div className="form-group">
              <div className="form-group-label-row">
                <label htmlFor="make-main-correct_answer">Правильный ответ</label>
                <button
                  type="button"
                  className="btn btn-secondary btn-regenerate-answer"
                  onClick={handleMakeMainRegenerateAnswer}
                  disabled={makeMainRegenerating || !(makeMainFormData.text || '').trim()}
                  title={!(makeMainFormData.text || '').trim() ? 'Сначала введите текст вопроса' : 'Пересоздать эталонный ответ нейросетью'}
                >
                  {makeMainRegenerating ? '⏳ Пересоздание...' : '🔄 Пересоздать ответ'}
                </button>
              </div>
              <textarea
                id="make-main-correct_answer"
                value={makeMainFormData.correct_answer}
                onChange={(e) => setMakeMainFormData({ ...makeMainFormData, correct_answer: e.target.value })}
                rows={4}
                placeholder="Правильный ответ (необязательно)"
                disabled={makeMainRegenerating}
              />
            </div>
            <div className="form-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={makeMainSaving || topics.length === 0}
              >
                {makeMainSaving ? 'Перенос...' : 'Сделать основным вопросом'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseMakeMain}
                disabled={makeMainSaving}
              >
                Отмена
              </button>
            </div>
          </form>
        </div>
      )}

      {totalPages > 1 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Страница {currentPage} из {totalPages}
          </div>
          <div className="pagination-controls">
            <button
              className="btn btn-secondary"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1 || loading}
            >
              ← Назад
            </button>
            <div className="pagination-pages">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) pageNum = i + 1;
                else if (currentPage <= 3) pageNum = i + 1;
                else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = currentPage - 2 + i;
                return (
                  <button
                    key={pageNum}
                    className={`btn pagination-page ${currentPage === pageNum ? 'active' : ''}`}
                    onClick={() => setCurrentPage(pageNum)}
                    disabled={loading}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              className="btn btn-secondary"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages || loading}
            >
              Вперед →
            </button>
          </div>
        </div>
      )}

      {showReorderModal && (
        <div className="reorder-modal-overlay" onClick={() => !reorderSaving && setShowReorderModal(false)}>
          <div className="reorder-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Изменить порядок вопросов</h3>
            {reorderStep === 'select' && (
              <>
                <p className="reorder-step-hint">Выберите тему и категорию, затем загрузите список вопросов для изменения порядка.</p>
                <div className="reorder-select-row">
                  <div className="form-group">
                    <label htmlFor="reorder-topic">Тема</label>
                    <select
                      id="reorder-topic"
                      value={reorderTopicId}
                      onChange={(e) => setReorderTopicId(e.target.value)}
                      className="filter-select"
                    >
                      <option value="">Выберите тему</option>
                      {topics.map((t) => (
                        <option key={t.id} value={t.id}>{t.title}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="reorder-category">Категория</label>
                    <select
                      id="reorder-category"
                      value={reorderCategory}
                      onChange={(e) => setReorderCategory(e.target.value)}
                      className="filter-select"
                    >
                      <option value="">Без категории</option>
                      <option value="Junior">Junior</option>
                      <option value="Middle">Middle</option>
                      <option value="Senior">Senior</option>
                    </select>
                  </div>
                  <div className="form-group reorder-load-btn">
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={loadReorderList}
                      disabled={!reorderTopicId || reorderLoading}
                    >
                      {reorderLoading ? 'Загрузка...' : 'Загрузить вопросы'}
                    </button>
                  </div>
                </div>
                <div className="reorder-modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowReorderModal(false)}>
                    Закрыть
                  </button>
                </div>
              </>
            )}
            {reorderStep === 'list' && (
              <>
                {reorderLoading ? (
                  <p>Загрузка...</p>
                ) : (
                  <>
                    <p className="reorder-step-hint">
                      Тема: <strong>{topics.find((t) => t.id === parseInt(reorderTopicId, 10))?.title ?? reorderTopicId}</strong>
                      {reorderCategory && <> · Категория: <strong>{reorderCategory}</strong></>}
                    </p>
                    {reorderList.length === 0 ? (
                      <p className="empty-state">Нет вопросов для выбранной темы и категории.</p>
                    ) : (
                    <div className="reorder-list">
                      <DndContext
                        sensors={reorderSensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleReorderDragEnd}
                      >
                        <SortableContext
                          items={reorderList.map((q) => q.id)}
                          strategy={verticalListSortingStrategy}
                        >
                          {reorderList.map((item, index) => (
                            <SortableReorderItem key={item.id} item={item} index={index} />
                          ))}
                        </SortableContext>
                      </DndContext>
                    </div>
                    )}
                    <div className="reorder-modal-actions">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={saveReorder}
                        disabled={reorderSaving || reorderList.length === 0}
                      >
                        {reorderSaving ? 'Сохранение...' : 'Сохранить'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setReorderStep('select')}
                        disabled={reorderSaving}
                      >
                        Назад
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setShowReorderModal(false)}
                        disabled={reorderSaving}
                      >
                        Закрыть
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default WelcomeQuestionsListTab;
